import { differenceInDays, parseISO } from 'date-fns';

/**
 * Canonical date helpers live in `~shared/helpers/datetime`. This module
 * re-exports them (single source of truth for the date-only / timezone-safe
 * set) and keeps the utilities that are unique to it, so existing
 * `~shared/utils/date-time` imports keep working unchanged.
 */
export {
  convertToUTCDate,
  normalizeAndConvertToUTCDate,
  calculateAge,
  getSession,
  calculateAgeFromUTC,
  calculateAgeFromISO,
  parseAndFormatTime,
  extractDateOnly,
  parseToNoonDate,
  formatDateOnlySafe,
  formatDateSafe,
  formatLocalDate,
  formatDateAsISO,
  toDateOnlyString,
  extractDateOnlyFromAPI,
  formatDateForDisplay,
  formatDateIgnoreTimezone,
} from '~shared/helpers/datetime';

interface Age {
  years: number;
  months: number;
}

export function isAgeInRange({
  age,
  startAge,
  endAge,
}: {
  age: Age;
  startAge: Age;
  endAge: Age;
}): boolean {
  // Convert age, startAge, and endAge to total months
  const ageInMonths = age.years * 12 + age.months;
  const startAgeInMonths = startAge.years * 12 + startAge.months;
  const endAgeInMonths = endAge.years * 12 + endAge.months;

  // Check if the age falls within the range
  return ageInMonths >= startAgeInMonths && ageInMonths <= endAgeInMonths;
}

export function convertTo12HourFormat(time24h?: string | null): string {
  if (!time24h) {
    return '';
  }

  const parts = time24h.split(':');
  if (parts.length < 2) {
    return '';
  }

  const [hoursStr, minutes = '00'] = parts;
  const hours = parseInt(hoursStr ?? '0', 10);

  if (Number.isNaN(hours)) {
    return '';
  }

  // Determine if it's AM or PM
  const period = hours >= 12 ? 'PM' : 'AM';

  // Convert hours to 12-hour format
  let hours12 = hours % 12;
  // If hours is 0, it should be displayed as 12 in 12-hour format
  hours12 = hours12 === 0 ? 12 : hours12;

  // Format hours with leading zero if needed
  const formattedHours = hours12 < 10 ? `0${hours12}` : `${hours12}`;

  // Return the formatted time
  return `${formattedHours}:${minutes} ${period}`;
}

export const calculateDateDifference = (startDate: string, endDate: string) => {
  const start = parseISO(startDate);
  const end = parseISO(endDate);
  return differenceInDays(end, start);
};

/**
 * Check if a string is a valid ISO date
 * @param str The string to check
 * @returns True if the string is a valid ISO date, false otherwise
 */
export function isValidIsoDate(str: string): boolean {
  // Regex to check for a basic ISO datetime format (YYYY-MM-DDTHH:mm:ss.sssZ
  // or with timezone offset). This is a general pattern, not exhaustive for all ISO 8601 variations.
  const isoDatePattern = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:[+-]\d{2}:\d{2}|Z)$/;

  if (!isoDatePattern.test(str)) {
    return false; // Fails format check
  }

  const date = new Date(str);

  // Check if the date object is valid (not NaN for getTime())
  if (isNaN(date.getTime())) {
    return false; // Fails semantic validity check
  }

  return true;
}
